'use client';

import {useEffect, useState} from 'react';
import LoadingScreen from '@/app/(app)/games/[gid]/_src/LoadingScreen';
import getGameId from '@/utils/getGameId';
import {datadogRum} from '@datadog/browser-rum';
import {NextJSError, SkillzAPIError} from '@/utils/customErrors';

type Props = {
  fallbackUrl: string;
  isApiError?: boolean;
  message?: string;
  context?: Record<string, unknown>;
  error?: Error & {digest: string};
};

export default function Error({fallbackUrl, error, isApiError, message, context}: Props) {
  const [shouldRenderReloadUI, setShouldRenderReloadUI] = useState(false);
  const reset = () => window.location.reload();
  const gameId = getGameId();
  const goToGameHome = () => {
    window.location.pathname = gameId === '0' ? fallbackUrl : `/games/${gameId}/home`;
  };

  const code = (context?.correlationId as string)?.substring(0, 8) ?? error?.digest ?? null;

  useEffect(() => {
    let timeoutId: NodeJS.Timeout;
    const reloadAttempts = parseInt(sessionStorage.getItem('reloadAttempts') || '0', 10);

    if (reloadAttempts < 2) {
      timeoutId = setTimeout(() => {
        sessionStorage.setItem('reloadAttempts', String(reloadAttempts + 1));
        reset();
      }, 7000);
    } else {
      setShouldRenderReloadUI(true);
      sessionStorage.removeItem('reloadAttempts');

      if (isApiError) {
        datadogRum.addError(new SkillzAPIError(message ?? 'Unexpected SkillzAPIError'), context);
      } else {
        datadogRum.addError(
          new NextJSError(message ?? error?.message ?? 'Unexpected NextJSError'),
          context
        );
      }
      // Send error message to React Native WebView
      if (typeof window !== 'undefined' && window.ReactNativeWebView) {
        console.log('Sending error message to React Native WebView');
        try {
          window.ReactNativeWebView.postMessage(
            JSON.stringify({
              platform: 'portal_play',
              type: 'unmount_webview',
              data: {
                gameId: gameId,
                error: true,
                message: message ?? error?.message ?? 'Unexpected error occurred',
                isApiError,
                code: code,
              },
            })
          );
        } catch (webViewError) {
          console.error('Failed to send error message to WebView:', webViewError);
        }
      }
    }

    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, [error, message, isApiError]);

  if (!shouldRenderReloadUI) {
    return (
      <div className='bg-gray-100 flex items-center justify-center h-screen'>
        <LoadingScreen />
      </div>
    );
  }

  return (
    <div className='bg-gray-100 flex items-center justify-center h-screen'>
      <div className='text-center'>
        <h2 className='mt-4 text-2xl font-semibold text-gray-800'>Error</h2>
        {shouldRenderReloadUI && (
          <>
            <p className='mt-2 text-gray-600 whitespace-break-spaces'>
              We&apos;re experiencing technical difficulties. <br />
              Please try again later.
              <br />
              <br />
              {code ? <span className='text-gray-400'>Code: {code}</span> : null}
            </p>
            <button
              onClick={() => goToGameHome()}
              className='mt-6 mr-2 inline-block bg-gradient-to-b from-[#1f8ed4] to-[#5ebffd] text-white px-6 py-2 rounded-lg shadow-md hover:bg-blue-600'
            >
              Go Back Home
            </button>

            <button
              onClick={() => reset()}
              className='mt-6 inline-block bg-gradient-to-b from-[#1f8ed4] to-[#5ebffd] text-white px-6 py-2 rounded-lg shadow-md hover:bg-blue-600'
            >
              Try again
            </button>
          </>
        )}
      </div>
    </div>
  );
}
